import { useEffect, useState } from 'react';

function quantise(intervalMs: number): number {
  return Math.floor(Date.now() / intervalMs) * intervalMs;
}

/**
 * Memo-friendly "now": returns the current timestamp quantised to `intervalMs`
 * (minute precision by default).
 *
 * Calling `Date.now()` in a render body produces a new value on every render,
 * which defeats any `useMemo` that lists it as a dependency. This hook returns
 * a referentially stable value within each interval, so memos keyed on "now"
 * only recompute when the quantised time actually advances. A timer scheduled
 * at the next interval boundary (not a poll) keeps the value fresh while the
 * component is mounted.
 */
export function useNow(intervalMs = 60_000): number {
  const [now, setNow] = useState(() => quantise(intervalMs));

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const schedule = (): void => {
      // Fire just after the next interval boundary; +1ms guards against
      // timers firing marginally early and re-reading the same interval.
      const delay = intervalMs - (Date.now() % intervalMs) + 1;
      timer = setTimeout(() => {
        const next = quantise(intervalMs);
        // Functional update: only re-renders when the quantised value moves.
        setNow((current) => (current === next ? current : next));
        schedule();
      }, delay);
    };
    // Catch up immediately in case the interval changed or the component
    // stayed unmounted-then-remounted across a boundary.
    setNow(quantise(intervalMs));
    schedule();
    return () => clearTimeout(timer);
  }, [intervalMs]);

  return now;
}
